import { offset } from '@popperjs/core';
import { add } from 'lodash';
import { Html5Event } from 'plyr';

import { type App } from '~/app';

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Floatier Island Component');

    const listeners: Array<[Document | Element | Window  , string, EventListenerOrEventListenerObject]> = [];

    app.afterMount
        .then(() => {
            // Desktop
            mountFloatierIsland(
                listeners,
                '.floatier-island',
                '.indicator',
                'active-tab-indicator',
                '#indicator-',
                '.indicator-container-desktop'
            );
            // Mobile
            mountFloatierIsland(
                listeners,
                '.floatier-island',
                '.indicator-mobile',
                'active-step-indicator',
                '#indicator-mobile-',
                '.indicator-container-mobile'
            );
        })
        .catch((error) => {
            throw new Error('[Stockland FE] Unable to register Stockland Floatier Island Component', error);
        });

    return function destroyFloatierIslandl() {
        unMountFloatierIsland(listeners);
        app.logger.debug('[DXA] Floatier Island Component destroyed');
    };
};

function mountFloatierIsland(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    componentSelector: string, // Used to find slider parent wrapper
    indicatorSelector: string, // Used to find indicators (useful when multiple handle the same slides)
    activeIndicatorClass: string,
    currentIndicatorPrefix: string,
    indicatorContainer: string,
    root: HTMLElement | null = null
) {
    let rootNode: HTMLElement | Document = document;
    if (root) rootNode = root;

    const floatingIslands = rootNode.querySelectorAll<HTMLElement>(componentSelector);

    for (const currentFloatingIsland of floatingIslands) {
        const islandIndicators = currentFloatingIsland.querySelectorAll<HTMLElement>(indicatorSelector);

        //setting up floating island
        let isIslandFixed = false;
        let initialIslandOffset = 0;
        const defaultIslandOffset = 96;
        // eslint-disable-next-line @typescript-eslint/no-loop-func
        window.addEventListener('scroll', () => {
            const floatIslandElement = currentFloatingIsland.querySelector<HTMLElement>(indicatorContainer);
            //try to find the offset of island from window's top before the position is fixed
            //once the position is fixed try to use the offset that was set earlier
            initialIslandOffset = isIslandFixed
                ? initialIslandOffset
                : (floatIslandElement?.getBoundingClientRect().top ?? 0) + window.scrollY;

            if (window.scrollY > initialIslandOffset - defaultIslandOffset) {
                floatIslandElement?.classList.add('fixed', 'left-0', 'right-0');
                isIslandFixed = true;
            } else {
                floatIslandElement?.classList.remove('fixed', 'left-0', 'right-0');
                isIslandFixed = false;
            }
        });

        //setup intersection observer

        const isMobileView = window.matchMedia("(max-width: 1024px)").matches;


        const options = {
            root: null,
            rootMargin: isMobileView ? '-40% 0px -10% 0px' : '-30% 0px -60% 0px',
            threshold: isMobileView ? 0.25: 0
        };

        const callback = (entries: any[]) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    //when section goes into the viewport
                    setActiveIsland(entry.target.id);
                }
            }
        };

        const observer = new IntersectionObserver(callback, options);
        //for all links on floating island
        for (const currentIndicator of islandIndicators) {
            const currentAnchorBlock = rootNode.querySelector<HTMLElement>(
                currentIndicator.dataset?.indicates ? `#${currentIndicator.dataset.indicates}` : ''
            );

            addIndicatorClickEvents(currentIndicator, currentAnchorBlock);
            //set observer for all section linked to floating island component
            if (currentAnchorBlock) {
                observer.observe(currentAnchorBlock);
            }
        }

        const removeAllActiveIndicator = () => {
            for (const indicator of islandIndicators) {
                indicator.classList.remove(activeIndicatorClass);
            }
        };

        const setActiveIsland = (targetId: string) => {
            removeAllActiveIndicator();
            const inactiveIndicator = currentFloatingIsland.querySelector<HTMLElement>(
                `${currentIndicatorPrefix}${targetId}`
            );
            inactiveIndicator?.classList.add(activeIndicatorClass);
        };

        let previousScrollpos = window.scrollY;
        const floatingIsland = rootNode.querySelector<HTMLElement>(".indicator-container-desktop")
        const ribbonBarParent = rootNode.querySelector<HTMLElement>(".ribbon-bar-parent")
        const topNavigation = rootNode.querySelector<HTMLElement>(".top-navigation-bar")
        if (floatingIsland && ribbonBarParent && topNavigation){
            window.addEventListener('scroll', function () {
                let ribbonBarParentHeight = 0;
                let topNavigationHeight = 0;
                const floatingIslandTopPadding = 16;
                const currentScrollPos = window.scrollY;
                ribbonBarParentHeight = ribbonBarParent.offsetHeight;
                topNavigationHeight = topNavigation.offsetHeight;
                floatingIsland.style.top = (previousScrollpos > currentScrollPos) ? `${ribbonBarParentHeight + topNavigationHeight + floatingIslandTopPadding }px` : `${topNavigationHeight + floatingIslandTopPadding}px`
                previousScrollpos = currentScrollPos;
            })
        }
    }
}

function addIndicatorClickEvents(indicator: HTMLElement, target: HTMLElement | null) {
    indicator.addEventListener('click', (event) => {
        event.preventDefault();
        if (target) {
            // Determine if we are in mobile view
            const isMobileView = window.matchMedia("(max-width: 1024px)").matches;

            // Get the current scroll position
            const currentScrollPos = window.scrollY;

            // Get the top position of the target relative to the viewport
            const targetPos = target.getBoundingClientRect().top;

            let offset = isMobileView ? 120 :230; // Default offset

            if (isMobileView) {
                // Find the floating island element by traversing up from the target
                const openMenu  = document.querySelector<HTMLElement>('.indicator-container-mobile');


                // If the floating island is found and it's a mobile view, adjust the offset
                if (openMenu) {
                    offset += openMenu.offsetHeight;
                }
            }

            // Calculate the desired scroll position with an offset
            const desiredScrollPos = currentScrollPos + targetPos - offset;

            // Smoothly scroll to the desired position
            window.scrollTo({
                top: desiredScrollPos,
                behavior: 'smooth'
            });

            if (isMobileView) {
                setTimeout(() => {
                    window.scrollTo({
                        top: desiredScrollPos,
                        behavior: 'smooth'
                    });
                }, 50);
            }


            history.pushState({}, '', `#${target.id}`);
        }
    });
}

function unMountFloatierIsland(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>
) {
    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}
